import React, { useMemo, useEffect, useState, useRef } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Card,
  Button,
  Dialog,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Backdrop,
  InputAdornment,
} from "@mui/material";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import { FieldArray, FormikProvider, useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloseIcon from "@mui/icons-material/Close";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import {
  useCreateStudentperentOnlyMutation,
  useCheckParentDuplicateMutation,
} from "../../../Api/attoDeskApi";
import { IStudentPerent } from "../../../Api/Interface/api.interface";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import CustomCheckbox from "../../../Components/Checkbox.tsx";
import ParentCredsDialog, { PortalCreds } from "./ParentCredsDialog";

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IStudentPerent;
  onSuccess?: (newParent: { id: number }) => void;
}

const Team = ({
  openModel = false,
  handleCloseDialog,
  data,
  onSuccess,
}: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [newCompany, { isLoading }] = useCreateStudentperentOnlyMutation();
  const [checkDuplicate] = useCheckParentDuplicateMutation();
  const isMobileRes = useIsMobile();

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Sanitize to digits only, hard-capped at 10 — the input can never hold more.
  const digitsOf = (v: string | undefined) => normalizePhoneNumber(v);

  // Newly-generated portal login (shown once) and the parent to hand back to the
  // create page once the admin dismisses the credentials dialog.
  const [creds, setCreds] = useState<PortalCreds | null>(null);
  const [pendingParent, setPendingParent] = useState<{ id: number } | null>(null);

  // Async duplicate-check state (kept outside formik).
  const [phoneDup, setPhoneDup] = useState(false);
  const [emailDup, setEmailDup] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Backend 422 field errors, shown under the matching input.
  const [backendErrors, setBackendErrors] = useState<{ phone?: string; email?: string }>({});
  // Monotonic request ids so a slow earlier check can't overwrite a newer result.
  const phoneReqId = useRef(0);
  const emailReqId = useRef(0);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const formik = useFormik<IStudentPerent>({
    initialValues: {
      id: Number(data?.id),
      first_name: data?.first_name || "",
      middle_name: data?.middle_name || "",
      last_name: data?.last_name || "",
      // Stored as raw digits; formatting is presentational elsewhere.
      contact_number: data?.contact_number ? digitsOf(data.contact_number) : "",
      email: data?.email || "",
      notes: data?.notes || "",
      is_email_notification: Boolean(data?.is_email_notification),
      is_sms_notification: Boolean(data?.is_sms_notification),
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setSubmitAttempted(true);
      setPhoneTouched(true);
      setEmailTouched(true);

      const digits = digitsOf(values.contact_number as string);
      const email = (values.email || "").trim();

      // Block submission while anything is invalid, duplicated, or still checking.
      if (
        digits.length !== 10 ||
        !EMAIL_REGEX.test(email) ||
        phoneDup ||
        emailDup ||
        phoneChecking ||
        emailChecking
      ) {
        return;
      }
      if (submitting || isLoading) return; // prevent double submission

      setSubmitting(true);
      try {
        const updatedData = {
          ...(data?.id ? { id: Number(values.id) } : {}),
          first_name: toProperCase(values.first_name),
          middle_name: toProperCase(values.middle_name),
          last_name: toProperCase(values.last_name),
          contact_number: digits,
          email: email.toLowerCase(),
          notes: values.notes,
          is_email_notification: values.is_email_notification,
          is_sms_notification: values.is_sms_notification,
        };

        const response = await newCompany(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
          return;
        }

        showMessage(response.message);
        const newParent = {
          id: response.data.id,
          first_name: values.first_name,
          middle_name: values.middle_name,
          last_name: values.last_name,
          contact_number: digits,
          email: email.toLowerCase(),
        };

        // A portal login is auto-generated on add — show it once before
        // handing the parent back to the create page (which closes this modal).
        const portal = (response as any).portal;
        if (portal?.temp_password) {
          setPendingParent(newParent);
          setCreds({ username: portal.username, password: portal.temp_password });
        } else {
          const portalError = (response as any).portal_error;
          if (portalError) {
            showErrorMessage(`Parent added, but no portal login was created: ${portalError}`);
          }
          if (newParent.id && onSuccess) {
            onSuccess(newParent);
          }
          handleClose();
        }
      } catch (error: any) {
        // Map backend 422 field errors under the matching inputs. Keep the modal
        // open and the user's data intact so they can correct and resubmit.
        const fieldErrors = error?.data?.errors;
        if (error?.status === 422 && fieldErrors) {
          setBackendErrors({
            phone: fieldErrors.phone?.[0] || fieldErrors.contact_number?.[0],
            email: fieldErrors.email?.[0],
          });
        } else {
          console.error("Parent create failed:", error);
          showErrorMessage(
            error?.data?.message || error?.message || error?.error || "Something went wrong"
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const phoneDigits = digitsOf(formik.values.contact_number as string);
  const emailValue = (formik.values.email || "").trim();

  // ── Debounced duplicate checks: only when the value is locally valid ──
  const debouncedPhone = useDebounce(phoneDigits, 400);
  const debouncedEmail = useDebounce(emailValue, 400);

  useEffect(() => {
    if (debouncedPhone.length !== 10) {
      setPhoneChecking(false);
      setPhoneDup(false);
      return;
    }
    const reqId = ++phoneReqId.current;
    setPhoneChecking(true);
    checkDuplicate({ contact_number: debouncedPhone, id: data?.id })
      .unwrap()
      .then((res: any) => {
        if (reqId === phoneReqId.current) setPhoneDup(Boolean(res?.data?.phone));
      })
      .catch(() => {
        if (reqId === phoneReqId.current) setPhoneDup(false);
      })
      .finally(() => {
        if (reqId === phoneReqId.current) setPhoneChecking(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPhone]);

  useEffect(() => {
    if (!EMAIL_REGEX.test(debouncedEmail)) {
      setEmailChecking(false);
      setEmailDup(false);
      return;
    }
    const reqId = ++emailReqId.current;
    setEmailChecking(true);
    checkDuplicate({ email: debouncedEmail, id: data?.id })
      .unwrap()
      .then((res: any) => {
        if (reqId === emailReqId.current) setEmailDup(Boolean(res?.data?.email));
      })
      .catch(() => {
        if (reqId === emailReqId.current) setEmailDup(false);
      })
      .finally(() => {
        if (reqId === emailReqId.current) setEmailChecking(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail]);

  // ── Single highest-priority message per field: required → format → duplicate → backend ──
  const phoneError = useMemo(() => {
    const show = phoneTouched || submitAttempted;
    if (show && phoneDigits.length !== 10) return "Enter a valid 10-digit contact number.";
    if (phoneDup) return "This phone number is already used by another parent.";
    if (backendErrors.phone) return backendErrors.phone;
    return "";
  }, [phoneTouched, submitAttempted, phoneDigits, phoneDup, backendErrors.phone]);

  const emailError = useMemo(() => {
    const show = emailTouched || submitAttempted;
    if (show && emailValue.length === 0) return "Email address is required.";
    if (show && !EMAIL_REGEX.test(emailValue)) return "Enter a valid email address.";
    if (emailDup) return "This email address is already used by another parent.";
    if (backendErrors.email) return backendErrors.email;
    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTouched, submitAttempted, emailValue, emailDup, backendErrors.email]);

  const namesMissing =
    !(formik.values.first_name || "").trim() || !(formik.values.last_name || "").trim();
  const phoneInvalid = phoneDigits.length !== 10;
  const emailInvalid = !EMAIL_REGEX.test(emailValue);
  const disableCreate =
    namesMissing ||
    phoneInvalid ||
    emailInvalid ||
    phoneChecking ||
    emailChecking ||
    phoneDup ||
    emailDup ||
    Boolean(backendErrors.phone) ||
    Boolean(backendErrors.email) ||
    isLoading ||
    submitting;

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      fullScreen={isMobileRes}
      fullWidth
      maxWidth="md"
      disableEnforceFocus
      PaperProps={{
        sx: {
          borderRadius: isMobileRes ? 0 : "12px",
          overflow: "visible",
        },
      }}
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid item pt={2} alignContent={"center"}>
                  <Typography className={classes.cardTitle}>
                    {data ? "Edit Parent" : "Create Parent"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  pt={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    className={classes.editButton}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Parent Information
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={2}>
                        <Grid item lg={4} md={4} sm={6} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                First Name{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="First Name"
                                size="small"
                                {...formik.getFieldProps("first_name")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={4} md={4} sm={6} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Middle Name{" "}
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Middle Name"
                                size="small"
                                {...formik.getFieldProps("middle_name")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item lg={4} md={4} sm={6} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Last Name{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Last Name"
                                size="small"
                                {...formik.getFieldProps("last_name")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Contact Information
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={2}>
                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Contact Number{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                fullWidth
                                placeholder="647-567-5666"
                                size="small"
                                type="tel"
                                // Always show the formatted value; state stays raw digits.
                                value={formatPhoneNumber(phoneDigits)}
                                // No maxLength: it would truncate a FORMATTED paste
                                // (e.g. "647-667-1647") before the sanitizer runs and
                                // silently drop digits. digitsOf(...).slice(0,10) is the
                                // real guard — the field can never hold an 11th digit.
                                inputProps={{
                                  inputMode: "numeric",
                                  "aria-label": "Contact Number",
                                }}
                                onChange={(e) => {
                                  // Sanitize before it ever reaches state — pasted or
                                  // formatted values can never leave more than 10 digits.
                                  formik.setFieldValue("contact_number", digitsOf(e.target.value));
                                  if (backendErrors.phone) {
                                    setBackendErrors((prev) => ({ ...prev, phone: undefined }));
                                  }
                                }}
                                onBlur={() => setPhoneTouched(true)}
                                className={classes.textField}
                                InputProps={{
                                  sx: { fontSize: 14 },
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <LocalPhoneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                                    </InputAdornment>
                                  ),
                                  endAdornment: phoneChecking ? (
                                    <InputAdornment position="end">
                                      <CircularProgress size={16} />
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                                error={Boolean(phoneError)}
                              />
                              {/* Reserved error space to keep the modal from jumping. */}
                              <Typography
                                sx={{
                                  minHeight: 18,
                                  mt: 0.25,
                                  fontSize: 12,
                                  lineHeight: 1.4,
                                  color: phoneError ? "error.main" : "text.secondary",
                                }}
                              >
                                {phoneError || (phoneChecking ? "Checking…" : " ")}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item lg={6} md={6} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Email Address{" "}
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                fullWidth
                                placeholder="email@example.com"
                                size="small"
                                type="email"
                                value={formik.values.email}
                                inputProps={{ "aria-label": "Email Address" }}
                                onChange={(e) => {
                                  formik.setFieldValue("email", e.target.value);
                                  if (backendErrors.email) {
                                    setBackendErrors((prev) => ({ ...prev, email: undefined }));
                                  }
                                }}
                                onBlur={() => setEmailTouched(true)}
                                className={classes.textField}
                                InputProps={{
                                  sx: { fontSize: 14 },
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <EmailIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                                    </InputAdornment>
                                  ),
                                  endAdornment: emailChecking ? (
                                    <InputAdornment position="end">
                                      <CircularProgress size={16} />
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                                error={Boolean(emailError)}
                              />
                              <Typography
                                sx={{
                                  minHeight: 18,
                                  mt: 0.25,
                                  fontSize: 12,
                                  lineHeight: 1.4,
                                  color: emailError ? "error.main" : "text.secondary",
                                }}
                              >
                                {emailError || (emailChecking ? "Checking…" : " ")}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid
                        container
                        justifyContent={"space-between"}
                        spacing={5}
                        sx={{ borderBottom: 2, padding: 2 }}
                        className={classes.titleGrid}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          pt={2}
                          alignContent={"center"}
                        >
                          <Typography className={classes.cardTitle}>
                            Notification
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={3} p={2}>
                        <Grid item>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <CustomCheckbox
                                  checked={formik.values.is_email_notification}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "is_email_notification",
                                      e.target.checked
                                    )
                                  }
                                  size="small"
                                />
                              }
                              label="Email"
                            />
                          </FormGroup>
                        </Grid>
                        <Grid item>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <CustomCheckbox
                                  checked={formik.values.is_sms_notification}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "is_sms_notification",
                                      e.target.checked
                                    )
                                  }
                                  size="small"
                                />
                              }
                              label="SMS"
                            />
                          </FormGroup>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Button
                          variant="contained"
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={disableCreate}
                        >
                          {(isLoading || submitting) && (
                            <CircularProgress
                              size={18}
                              className={classes.isLoading}
                              sx={{ marginRight: 1 }}
                            />
                          )}
                          {data ? "Update" : "Create"}
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          className={classes.addButton}
                          onClick={handleClose}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Backdrop
                  sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 2000,
                  })}
                  open={isLoading}
                >
                  <CircularProgress color="inherit" />
                </Backdrop>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
    <ParentCredsDialog
      creds={creds}
      onClose={() => {
        setCreds(null);
        if (pendingParent?.id && onSuccess) {
          onSuccess(pendingParent);
        }
        setPendingParent(null);
        handleClose();
      }}
    />
    </>
  );
};

export default Team;
